import { useContext, useEffect } from 'react';
import { LoaderContext } from '@contexts/loader/loader-context';
import {
  MutationFunction,
  MutationKey,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { RequestError } from '@utils/request/request.type';
import { showToastErrorRequest } from '@utils/show-toast-error-request/show-toast-error-request';

import { useRefreshTokenHandler } from './auth/use-refresh-token-handler';
import { useToast } from './use-toast/use-toast';

export const useGenericMutation = <TData = unknown, TError = RequestError, TVariables = void, TContext = unknown>(
  key: MutationKey,
  fn: MutationFunction<TData, TVariables>,
  options?: Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn' | 'mutationKey'>,
  handleLoading?: boolean,
  hideError?: boolean
): UseMutationResult<TData, TError, TVariables, TContext> => {
  const { decrementLoaderQueue, incrementLoaderQueue } = useContext(LoaderContext);
  const toast = useToast();
  const requestId = `mutation-${key}`;

  const { createRefreshAndRetryHandler } = useRefreshTokenHandler();
  const handleRefreshAndRetry = createRefreshAndRetryHandler<TVariables, TData | Promise<TData>>((variables) =>
    fn(variables)
  );

  // MUTATION FUNCTION WITH IMMEDIATE RETRY
  const mutationFnWithRetry = async (variables: TVariables): Promise<TData> => {
    try {
      return await fn(variables);
    } catch (error: unknown) {
      if (error instanceof RequestError && error.status === 401) {
        return await handleRefreshAndRetry(variables);
      }
      // If not 401, propagate the error
      throw error;
    }
  };

  const mutation = useMutation(key, mutationFnWithRetry, {
    ...options,
    onSuccess: (data, variables, context) => {
      if (options?.onSuccess) options.onSuccess(data, variables, context);
      decrementLoaderQueue(requestId);
    },
    onError: (error, variables, context) => {
      // 401 errors are now handled in mutationFnWithRetry, only handle other errors here
      if (options?.onError) options.onError(error, variables, context);

      if (error instanceof RequestError) {
        if (!hideError && showToastErrorRequest(error)) {
          toast.push({
            title: error.message,
            text: '',
            type: 'error',
            disableTimeOut: true,
          });
        }
      }
      decrementLoaderQueue(requestId);
    },
    onSettled: (data, error, variables, context) => {
      if (options?.onSettled) options.onSettled(data, error, variables, context);
      decrementLoaderQueue(requestId);
    },
  });

  useEffect(() => {
    if (handleLoading) return;
    if (mutation.isLoading) {
      incrementLoaderQueue(requestId);
    }
    return () => {
      if (mutation.isLoading) {
        decrementLoaderQueue(requestId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutation.isLoading, handleLoading]);

  return mutation;
};
